import { Search, SlidersHorizontal, X, Star } from 'lucide-react';
import type { Category, CatalogFilters as Filters } from '../types';
import { cn } from '../lib/utils';

interface CatalogFiltersProps {
    filters: Filters;
    categories: Category[];
    resultCount: number;
    onChange: (filters: Filters) => void;
}

const ENVIRONMENT_OPTIONS = [
    { value: null, label: 'Tous environnements' },
    { value: 'prod' as const, label: 'Production' },
    { value: 'staging' as const, label: 'Recette' },
    { value: 'dev' as const, label: 'Développement' },
];

const NETWORK_OPTIONS = [
    { value: null, label: 'Tous réseaux' },
    { value: 'public' as const, label: 'Réseau public' },
    { value: 'private' as const, label: 'Réseau interne' },
];

export function CatalogFilters({
    filters,
    categories,
    resultCount,
    onChange,
}: CatalogFiltersProps) {
    const update = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });

    const hasActiveFilters =
        filters.search !== '' ||
        filters.category !== null ||
        filters.environment !== null ||
        filters.network !== null ||
        filters.favoritesOnly;

    const reset = () =>
        onChange({
            search: '',
            category: null,
            environment: null,
            network: null,
            favoritesOnly: false,
        });

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
                {/* La recherche est le point d'entree reel du catalogue : vingt-trois
                    applications se parcourent mal a l'oeil. Elle occupe donc la
                    largeur et une hauteur superieure aux autres controles, au lieu
                    d'etre un champ parmi six. */}
                <div className="search-field relative flex-1">
                    <Search className="search-field-icon" aria-hidden />
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(e) => update({ search: e.target.value })}
                        placeholder="Rechercher une application, une description, une étiquette…"
                        className="input search-field-input"
                        aria-label="Rechercher une application"
                    />
                    {filters.search !== '' && (
                        <button
                            type="button"
                            onClick={() => update({ search: '' })}
                            className="search-field-clear"
                            aria-label="Effacer la recherche"
                        >
                            <X className="w-4 h-4" aria-hidden />
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    onClick={() => update({ favoritesOnly: !filters.favoritesOnly })}
                    className={cn('btn', filters.favoritesOnly ? 'btn-primary' : 'btn-secondary')}
                    aria-pressed={filters.favoritesOnly}
                >
                    <Star
                        className={cn('w-4 h-4', filters.favoritesOnly && 'fill-current')}
                        aria-hidden
                    />
                    Favoris
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={filters.category ?? ''}
                    onChange={(e) => update({ category: e.target.value || null })}
                    className="select w-auto"
                    aria-label="Filtrer par catégorie"
                >
                    <option value="">Toutes catégories</option>
                    {categories.map((category) => (
                        <option key={category.slug} value={category.slug}>
                            {category.name}
                            {category.applicationCount !== undefined
                                ? ` (${category.applicationCount})`
                                : ''}
                        </option>
                    ))}
                </select>

                <select
                    value={filters.environment ?? ''}
                    onChange={(e) =>
                        update({ environment: (e.target.value || null) as Filters['environment'] })
                    }
                    className="select w-auto"
                    aria-label="Filtrer par environnement"
                >
                    {ENVIRONMENT_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value ?? ''}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <select
                    value={filters.network ?? ''}
                    onChange={(e) =>
                        update({ network: (e.target.value || null) as Filters['network'] })
                    }
                    className="select w-auto"
                    aria-label="Filtrer par réseau"
                >
                    {NETWORK_OPTIONS.map((option) => (
                        <option key={option.label} value={option.value ?? ''}>
                            {option.label}
                        </option>
                    ))}
                </select>

                {hasActiveFilters && (
                    <button type="button" onClick={reset} className="btn btn-ghost">
                        <X className="w-4 h-4" aria-hidden />
                        Réinitialiser
                    </button>
                )}

                <p
                    className="ml-auto text-sm text-[var(--color-text-secondary)] flex items-center gap-1.5"
                    aria-live="polite"
                >
                    <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden />
                    {resultCount} application{resultCount > 1 ? 's' : ''}
                </p>
            </div>
        </div>
    );
}
